import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import { BookOpen, Heart, Lightbulb, Loader2, RefreshCw, ShoppingCart } from "lucide-react";
import type { CampaignMixAllocation, ContentPillarAllocation } from "@runcheetah/shared";
import { CONTENT_PILLAR_METADATA } from "@runcheetah/shared";
import type { AppRouter } from "@runcheetah/api/router";

import { CenteredLoader } from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { deriveVoiceBasics } from "@/lib/brand-onboarding-display";
import { applyNormalizedWeights, percentagesToWeights } from "@/lib/distribution";
import { hasReasoningWarning, toStrategyEditorState } from "@/lib/strategy";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@runcheetah/ui";
import { toast } from "sonner";

type PillMeta = {
  icon: typeof Lightbulb;
  tileClassName: string;
  iconClassName: string;
  barClassName: string;
};

const CONTENT_PILLAR_STYLES: Record<string, PillMeta> = {
  awareness: {
    icon: Lightbulb,
    tileClassName: "bg-lime-100/90",
    iconClassName: "text-lime-700",
    barClassName: "bg-lime-400",
  },
  education: {
    icon: BookOpen,
    tileClassName: "bg-amber-100",
    iconClassName: "text-amber-700",
    barClassName: "bg-amber-300",
  },
  trust: {
    icon: Heart,
    tileClassName: "bg-sky-100",
    iconClassName: "text-sky-700",
    barClassName: "bg-sky-400",
  },
  conversion: {
    icon: ShoppingCart,
    tileClassName: "bg-violet-100",
    iconClassName: "text-violet-700",
    barClassName: "bg-violet-400",
  },
};

const DEFAULT_PILL_STYLE: PillMeta = {
  icon: Lightbulb,
  tileClassName: "bg-slate-100",
  iconClassName: "text-slate-700",
  barClassName: "bg-slate-400",
};

type RouterOutputs = inferRouterOutputs<AppRouter>;
type RouterBrandVoice = NonNullable<RouterOutputs["brand"]["getBrandVoice"]>;
type RouterBrandProfile = NonNullable<RouterOutputs["brand"]["getBrandProfile"]>;
type RouterStrategy = NonNullable<RouterOutputs["strategy"]["getMine"]>;

export function FocusEditor() {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const { brand, brandQuery } = useRouteBrand();
  const hasCompletedScan = brand?.status === "scanned";

  const strategyQuery = trpc.strategy.getMine.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id && hasCompletedScan),
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );
  const voiceQuery = trpc.brand.getBrandVoice.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0, refetchOnWindowFocus: true },
  );
  const profileQuery = trpc.brand.getBrandProfile.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0, refetchOnWindowFocus: true },
  );

  const strategy: RouterStrategy | null = strategyQuery.data ?? null;
  const voice: RouterBrandVoice | null = voiceQuery.data ?? null;
  const profile: RouterBrandProfile | null = profileQuery.data ?? null;

  const [postsPerDay, setPostsPerDay] = useState(1);
  const [contentPillars, setContentPillars] = useState<ContentPillarAllocation[]>([]);
  const [campaignMix, setCampaignMix] = useState<CampaignMixAllocation[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!strategy || isDirty) {
      return;
    }

    const next = toStrategyEditorState(strategy);
    setPostsPerDay(next.postsPerDay);
    setContentPillars(next.contentPillars);
    setCampaignMix(next.campaignMix);
  }, [isDirty, strategy]);

  const updateStrategy = trpc.strategy.updateMine.useMutation({
    onSuccess: async () => {
      setIsDirty(false);
      if (!brand?.id) return;
      await utils.strategy.getMine.invalidate({ brandId: brand.id });
    },
  });

  const regenerateReasoning = trpc.strategy.regenerateReasoning.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;
      await utils.strategy.getMine.invalidate({ brandId: brand.id });
    },
  });

  const persistCurrentStrategy = async () => {
    if (!brand?.id) {
      return;
    }

    await updateStrategy.mutateAsync({
      brandId: brand.id,
      strategy: {
        postsPerDay,
        contentPillars,
        campaignMix,
      },
    });
  };

  const displayStatusLabel = intl.formatMessage({
    id: "focusEditor.readyStatus",
    defaultMessage: "Ready",
  });
  const brandName =
    profile?.brandName ??
    brand?.sourceDomain ??
    intl.formatMessage({ id: "focusEditor.yourBrand", defaultMessage: "Your brand" });
  const voicePills = voice
    ? deriveVoiceBasics(voice).map(({ label, value }) => `${label}: ${value}`)
    : [];
  const primaryAudiencePills = profile?.primaryAudienceSegments ?? [];
  const secondaryAudiencePills = profile?.secondaryAudienceSegments ?? [];

  if (
    brandQuery.isPending ||
    voiceQuery.isPending ||
    profileQuery.isPending ||
    (brand?.id && hasCompletedScan && strategyQuery.isPending)
  ) {
    return <CenteredLoader />;
  }

  if (!brand) {
    return <Navigate to="/app" replace />;
  }

  if (!hasCompletedScan) {
    return <Navigate to={getUnavailableBrandPath(brand.id)} replace />;
  }

  if (!strategy) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-5xl items-center justify-center px-6 py-8">
        <div className="w-full max-w-xl rounded-3xl border border-border/70 bg-card p-8 shadow-sm">
          <h2 className="text-xl font-semibold text-foreground">
            <FormattedMessage id="focusEditor.generatingFocus" defaultMessage="Generating focus" />
          </h2>
          <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <FormattedMessage
              id="focusEditor.generatingFocusDescription"
              defaultMessage="We are turning your brand profile into an initial focus."
            />
          </div>
        </div>
      </div>
    );
  }

  const heroCopy = buildHeroCopy({
    intl,
    brandName,
    brand,
    profile,
    strategy,
    audiencePills: primaryAudiencePills,
    voice,
  });

  const handleSaveFocus = async () => {
    try {
      await persistCurrentStrategy();
    } catch {
      toast.error(
        intl.formatMessage({
          id: "common.couldNotSaveChanges",
          defaultMessage: "Could not save changes",
        }),
      );
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-5 sm:px-6">
      <div className="flex items-center justify-end gap-3">
        <StrategyStatusPill label={displayStatusLabel} />
        {isDirty ? (
          <button
            type="button"
            onClick={() => void handleSaveFocus()}
            disabled={updateStrategy.isPending}
            className="rounded-xl bg-lime-300 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_10px_28px_rgba(200,255,0,.24)] transition hover:-translate-y-0.5 hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {updateStrategy.isPending
              ? intl.formatMessage({ id: "focusEditor.saving", defaultMessage: "Saving..." })
              : intl.formatMessage({
                  id: "focusEditor.saveStrategy",
                  defaultMessage: "Save strategy",
                })}
          </button>
        ) : (
          <div className="rounded-xl border border-border/70 bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm">
            <FormattedMessage id="focusEditor.savedState" defaultMessage="Strategy saved" />
          </div>
        )}
      </div>

      <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
        <div className="space-y-5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                <FormattedMessage id="focusEditor.eyebrow" defaultMessage="Strategy" />
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => regenerateReasoning.mutate({ brandId: brand.id })}
                disabled={regenerateReasoning.isPending}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {intl.formatMessage({
                  id: "focusEditor.refreshExplanation",
                  defaultMessage: "Refresh explanation",
                })}
              </Button>
            </div>
            <h1 className="max-w-2xl text-balance text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
              <FormattedMessage id="focusEditor.pageTitle" defaultMessage="Content Focus" />
            </h1>
            <p className="max-w-3xl text-[15px] font-medium leading-6 text-slate-800">
              {heroCopy.headline}
            </p>
            <p className="max-w-3xl text-sm leading-6 text-slate-600">{heroCopy.focus}</p>
            <p className="max-w-3xl text-xs leading-5 text-slate-500">{heroCopy.provenance}</p>
            <p className="max-w-3xl text-xs leading-5 text-slate-500">
              {hasReasoningWarning(strategy)
                ? intl.formatMessage({
                    id: "focusEditor.reasoningOutOfDate",
                    defaultMessage:
                      "The explanation is out of date. You can refresh it without blocking campaign generation.",
                  })
                : intl.formatMessage({
                    id: "focusEditor.reasoningFresh",
                    defaultMessage: "This explanation is in sync with the current strategy.",
                  })}
            </p>
          </div>

          <div className="space-y-4">
            <ChipRow
              label={intl.formatMessage({ id: "focusEditor.voice", defaultMessage: "Voice" })}
              chips={voicePills}
              emptyLabel={intl.formatMessage({
                id: "focusEditor.voiceEmpty",
                defaultMessage: "No voice signals yet",
              })}
            />
            <ChipRow
              label={intl.formatMessage({
                id: "focusEditor.primaryAudience",
                defaultMessage: "Primary audience",
              })}
              chips={primaryAudiencePills}
              emptyLabel={intl.formatMessage({
                id: "focusEditor.audienceEmpty",
                defaultMessage: "No primary audience segments yet",
              })}
            />
            <ChipRow
              label={intl.formatMessage({
                id: "focusEditor.secondaryAudience",
                defaultMessage: "Secondary audience",
              })}
              chips={secondaryAudiencePills}
              emptyLabel={intl.formatMessage({
                id: "focusEditor.secondaryAudienceEmpty",
                defaultMessage: "No secondary audience segments",
              })}
            />
          </div>
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-gradient-to-b from-lime-50 via-stone-50 to-stone-100 p-4 shadow-[0_18px_45px_-32px_rgba(13,31,45,.35)]">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
            <FormattedMessage id="focusEditor.postingCadence" defaultMessage="Posting cadence" />
          </p>
          <div className="relative mb-3">
            <div className="absolute -left-4 top-2 h-14 w-20 rounded-full bg-lime-300/30 blur-3xl" />
            <div className="relative flex items-center gap-3">
              <span className="text-4xl font-semibold tracking-[-0.06em] text-slate-950">
                {postsPerDay}
              </span>
              <span className="pt-2 text-sm font-medium text-slate-600">
                {postsPerDay === 1
                  ? intl.formatMessage({
                      id: "focusEditor.postPerDay",
                      defaultMessage: "post / day",
                    })
                  : intl.formatMessage({
                      id: "focusEditor.postsPerDay",
                      defaultMessage: "posts / day",
                    })}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <StepButton
                  ariaLabel={intl.formatMessage({
                    id: "focusEditor.decreasePostsPerDay",
                    defaultMessage: "Decrease posts per day",
                  })}
                  onClick={() => {
                    setIsDirty(true);
                    setPostsPerDay((current) => Math.max(1, current - 1));
                  }}
                  disabled={postsPerDay <= 1}
                >
                  {"-"}
                </StepButton>
                <StepButton
                  ariaLabel={intl.formatMessage({
                    id: "focusEditor.increasePostsPerDay",
                    defaultMessage: "Increase posts per day",
                  })}
                  onClick={() => {
                    setIsDirty(true);
                    setPostsPerDay((current) => current + 1);
                  }}
                >
                  {"+"}
                </StepButton>
              </div>
            </div>
          </div>
          <p className="text-xs leading-5 text-slate-600">
            <FormattedMessage
              id="focusEditor.postingCadenceDescription"
              defaultMessage="Maximum posts that can be scheduled on a single day across all campaigns."
            />
          </p>
        </aside>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <SectionHeader
          icon={<Lightbulb className="h-4 w-4 text-lime-700" />}
          iconClassName="bg-lime-100"
          title={intl.formatMessage({
            id: "focusEditor.contentBalance",
            defaultMessage: "Content balance",
          })}
          description={intl.formatMessage({
            id: "focusEditor.contentBalanceDescription",
            defaultMessage: "Cheetah uses these weights to decide what to generate more often.",
          })}
        />
        <DistributionSection
          items={contentPillars}
          metadata={CONTENT_PILLAR_METADATA}
          styles={CONTENT_PILLAR_STYLES}
          onAdjust={(index, delta) => {
            setIsDirty(true);
            setContentPillars((existing) => {
              const currentWeights = percentagesToWeights(existing.map((item) => item.percentage));
              const nextWeights = currentWeights.map((value, currentIndex) =>
                currentIndex === index ? clampWeight(value + delta) : value,
              );
              return applyNormalizedWeights(existing, nextWeights);
            });
          }}
        />
        <p className="mt-5 inline-flex items-center gap-2 text-xs text-slate-500">
          <span className="flex h-4 w-4 items-center justify-center rounded-full border border-slate-400 text-[10px] font-semibold text-slate-400">
            {"i"}
          </span>
          <FormattedMessage
            id="focusEditor.contentBalanceFootnote"
            defaultMessage="These weights adapt over time based on performance."
          />
        </p>
      </section>
      <div className="flex flex-wrap items-center gap-3">
        {!isDirty ? (
          <div className="rounded-xl border border-border/70 bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm">
            <FormattedMessage id="focusEditor.savedState" defaultMessage="Strategy saved" />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void handleSaveFocus()}
            disabled={updateStrategy.isPending}
            className="rounded-xl bg-lime-300 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_10px_28px_rgba(200,255,0,.24)] transition hover:-translate-y-0.5 hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {updateStrategy.isPending
              ? intl.formatMessage({ id: "focusEditor.saving", defaultMessage: "Saving..." })
              : intl.formatMessage({
                  id: "focusEditor.saveStrategy",
                  defaultMessage: "Save strategy",
                })}
          </button>
        )}
        {isDirty ? (
          <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
            <FormattedMessage id="focusEditor.unsavedChanges" defaultMessage="Unsaved changes" />
          </span>
        ) : null}
      </div>
    </div>
  );
}

function StrategyStatusPill({ label }: { label: string | null }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800">
      <span className="h-2 w-2 rounded-full bg-emerald-600" />
      {label}
    </div>
  );
}

function SectionHeader({
  icon,
  iconClassName,
  title,
  description,
}: {
  icon: ReactNode;
  iconClassName: string;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-3 flex items-start gap-3">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-xl", iconClassName)}>
        {icon}
      </div>
      <div>
        <h2 className="text-base font-semibold tracking-[-0.02em] text-slate-950">{title}</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function DistributionSection<T extends { key: string; percentage: number }>({
  items,
  metadata,
  styles,
  onAdjust,
}: {
  items: T[];
  metadata: Record<string, { label: string; description: string }>;
  styles: Record<string, PillMeta>;
  onAdjust: (index: number, delta: number) => void;
}) {
  const sorted = [...items]
    .map((item, index) => ({ item, index }))
    .toSorted((a, b) => b.item.percentage - a.item.percentage);

  return (
    <div className="divide-y divide-slate-100">
      {sorted.map(({ item, index }) => {
        const meta = metadata[item.key] ?? {
          label: item.key,
          description: item.key,
        };
        const style: PillMeta = styles[item.key] ?? DEFAULT_PILL_STYLE;
        const Icon = style.icon;

        return (
          <div
            key={item.key}
            className="grid gap-3 py-3 md:grid-cols-[40px_minmax(0,1.05fr)_minmax(0,1.5fr)_56px] md:items-center"
          >
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-xl",
                style.tileClassName,
              )}
            >
              <Icon className={cn("h-4 w-4", style.iconClassName)} />
            </div>

            <div className="min-w-0">
              <p className="text-sm font-semibold tracking-[-0.015em] text-slate-950">
                {meta.label}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-500">{meta.description}</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-200",
                    style.barClassName,
                  )}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
              <div className="flex items-center gap-1 rounded-full bg-white p-1 shadow-sm ring-1 ring-slate-200">
                <MiniStepButton
                  onClick={() => onAdjust(index, -5)}
                  ariaLabel={`Decrease ${meta.label}`}
                >
                  {"-"}
                </MiniStepButton>
                <MiniStepButton
                  onClick={() => onAdjust(index, 5)}
                  ariaLabel={`Increase ${meta.label}`}
                >
                  {"+"}
                </MiniStepButton>
              </div>
            </div>

            <div className="text-right text-sm font-semibold tracking-[-0.015em] text-slate-950">
              {item.percentage}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChipRow({
  label,
  chips,
  emptyLabel,
}: {
  label: string;
  chips: string[];
  emptyLabel: string;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <p className="min-w-20 pt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {chips.length ? (
          chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-800"
            >
              {toTitleCase(chip)}
            </span>
          ))
        ) : (
          <p className="pt-1 text-sm text-slate-500">{emptyLabel}</p>
        )}
      </div>
    </div>
  );
}

function StepButton({
  children,
  ariaLabel,
  onClick,
  disabled,
}: {
  children: ReactNode;
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-lg font-medium text-slate-900 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function MiniStepButton({
  children,
  ariaLabel,
  onClick,
}: {
  children: ReactNode;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded-full text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
    >
      {children}
    </button>
  );
}

function buildHeroCopy({
  intl,
  brandName,
  brand,
  profile,
  strategy,
  audiencePills,
  voice,
}: {
  intl: ReturnType<typeof useIntl>;
  brandName: string;
  brand: ReturnType<typeof useRouteBrand>["brand"];
  profile: RouterBrandProfile | null;
  strategy: RouterStrategy;
  audiencePills: string[];
  voice: RouterBrandVoice | null;
}) {
  const voiceSummary = voice
    ? deriveVoiceBasics(voice)
        .slice(0, 2)
        .map(({ value }) => toTitleCase(value))
        .join(" and ")
    : null;
  const audienceSummary = audiencePills[0] ?? profile?.marketCategory ?? null;
  const headline =
    strategy.reasoning?.summary ||
    intl.formatMessage(
      {
        id: "focusEditor.defaultHeadline",
        defaultMessage: "Grow {brandName} through clear, strategic social content.",
      },
      { brandName },
    );

  const focus = audienceSummary
    ? intl.formatMessage(
        {
          id: "focusEditor.defaultFocusWithAudience",
          defaultMessage:
            "Focused on reaching {audience} with a repeatable message system that keeps the brand sharp and useful.",
        },
        { audience: audienceSummary },
      )
    : intl.formatMessage({
        id: "focusEditor.defaultFocus",
        defaultMessage:
          "Focused on building a repeatable message system that keeps the brand sharp, useful, and easy to recognize.",
      });

  const provenance = intl.formatMessage(
    {
      id: "focusEditor.defaultProvenance",
      defaultMessage: "Built from your website, positioning, audience, and connected channels.",
    },
    {
      voicePart: voiceSummary ? `${voiceSummary.toLowerCase()}, ` : "",
      domain: brand?.sourceDomain ?? brandName,
    },
  );

  return { headline, focus, provenance };
}

function clampWeight(value: number) {
  return Math.max(0, Math.min(100, value));
}

function toTitleCase(value: string) {
  return value
    .split(/\s+/u)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}
